import { NavLink as RouterNavLink, NavLinkProps, useLocation } from "react-router-dom";
import { forwardRef, MouseEvent, useContext } from "react";
import { cn } from "@/lib/utils";
import { ExamModeContext } from "@/contexts/ExamModeContext";

interface NavLinkCompatProps extends Omit<NavLinkProps, "className"> {
  className?: string;
  activeClassName?: string;
  pendingClassName?: string;
}

const NavLink = forwardRef<HTMLAnchorElement, NavLinkCompatProps>(
  ({ className, activeClassName, pendingClassName, to, onClick, ...props }, ref) => {
    const examCtx = useContext(ExamModeContext);
    const location = useLocation();
    
    const isExamMode = examCtx?.isExamMode ?? false;
    const selectedDocument = examCtx?.selectedDocument ?? null;
    const isExamFinished = examCtx?.isExamFinished ?? false;
    const examTasks = examCtx?.examTasks ?? [];
    
    // Check if exam is in progress
    const isExamInProgress = isExamMode && !!selectedDocument && !isExamFinished && examTasks.length > 0;
    
    // Get the target path as string
    const targetPath = typeof to === 'string' ? to : to.pathname || '';
    
    // Check if we're navigating away from the current page
    const isNavigatingAway = location.pathname !== targetPath;
    
    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      // If exam is in progress and trying to navigate away, intercept
      if (isExamInProgress && isNavigatingAway) {
        e.preventDefault();
        // Dispatch custom event to trigger the confirmation dialog
        window.dispatchEvent(
          new CustomEvent('exam-navigation-attempt', { 
            detail: { path: targetPath } 
          })
        );
        return;
      }
      
      // Otherwise, call original onClick if provided
      onClick?.(e);
    };
    
    return (
      <RouterNavLink
        ref={ref}
        to={to}
        onClick={handleClick}
        className={({ isActive, isPending }) =>
          cn(className, isActive && activeClassName, isPending && pendingClassName)
        }
        {...props}
      />
    );
  },
);

NavLink.displayName = "NavLink";

export { NavLink };
